import { useEffect, useRef, useState } from "react";
import { getTodayHK } from "../lib/date";
import { AppMenu } from "./AppMenu";

type Metal = "gold" | "platinum" | "grain" | "gold_gram";

interface PriceRow {
  id: number;
  date: string;
  metal: Metal;
  sell_hkd: number;
  sell_mop: number;
  sell_cny: number | null;
  buy_hkd: number;
  buy_mop: number;
  buy_cny: number | null;
  created_at: string;
}

interface TodayResponse {
  date: string;
  items: PriceRow[];
}

interface MetalDisplay {
  label: string;
  subLabel?: string;
  metal: Metal;
}

const metalsForBoard: MetalDisplay[] = [
  { metal: "gold", label: "千足金", subLabel: "999.9" },
  { metal: "platinum", label: "鉑金", subLabel: "999.5" },
  { metal: "grain", label: "金粒" },
  { metal: "gold_gram", label: "千足金(克)", subLabel: "999.9" }
];

function formatPrice(value: number) {
  return Math.round(value).toLocaleString("zh-HK");
}

function samePriceData(a: TodayResponse | null, b: TodayResponse) {
  if (!a || a.date !== b.date || a.items.length !== b.items.length) {
    return false;
  }

  return b.items.every((next) => {
    const current = a.items.find((item) => item.metal === next.metal);
    return (
      current?.sell_hkd === next.sell_hkd &&
      current.sell_mop === next.sell_mop &&
      current.sell_cny === next.sell_cny &&
      current.buy_hkd === next.buy_hkd &&
      current.buy_mop === next.buy_mop &&
      current.buy_cny === next.buy_cny
    );
  });
}

function AnimatedPrice({
  loading,
  value
}: {
  loading: boolean;
  value: number | null | undefined;
}) {
  const [displayValue, setDisplayValue] = useState<number | undefined>(undefined);
  const displayValueRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (value == null) {
      displayValueRef.current = undefined;
      setDisplayValue(undefined);
      return;
    }

    const startValue = displayValueRef.current ?? 0;
    if (startValue === value) {
      displayValueRef.current = value;
      setDisplayValue(value);
      return;
    }

    const duration = 850;
    const startTime = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + (value - startValue) * eased;

      displayValueRef.current = nextValue;
      setDisplayValue(nextValue);

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        displayValueRef.current = value;
        setDisplayValue(value);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  if (loading && value == null) return "0";
  if (value == null || displayValue === undefined) return "—";
  return formatPrice(displayValue);
}

interface PriceTableProps {
  displayMode?: "default" | "signage";
}

export function PriceTable({ displayMode = "default" }: PriceTableProps) {
  const [data, setData] = useState<TodayResponse | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const refreshIntervalMs = displayMode === "signage" ? 15000 : 30000;

  const load = async (
    activeRef: { active: boolean },
    options: { initial?: boolean } = {}
  ) => {
    if (activeRef.active && options.initial) setInitialLoading(true);
    try {
      const res = await fetch(`/api/prices/today?date=${encodeURIComponent(getTodayHK())}`, { cache: "no-store" });
      if (!res.ok) throw new Error("failed");
      const json = (await res.json()) as TodayResponse;
      if (activeRef.active) {
        setData((current) => (samePriceData(current, json) ? current : json));
      }
    } catch {
      // Keep the last visible prices during background refresh failures.
    } finally {
      if (activeRef.active && options.initial) setInitialLoading(false);
    }
  };

  useEffect(() => {
    const activeRef = { active: true };
    void load(activeRef, { initial: true });

    const onRefresh = () => void load(activeRef);
    const intervalId = window.setInterval(() => void load(activeRef), refreshIntervalMs);

    window.addEventListener("skt:refresh-data", onRefresh);
    return () => {
      activeRef.active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("skt:refresh-data", onRefresh);
    };
  }, []);

  const findByMetal = (metal: Metal) =>
    data?.items.find((item) => item.metal === metal);

  return (
    <div
      className={`price-board ${displayMode === "signage" ? "price-board--signage" : ""}`}
    >
      <header className="price-board-header price-board-header--logo">
        <img
          className="brand-logo"
          src={`/logo.png?v=${__BUILD_ID__}`}
          alt="純金店 Seoun Kam Tim"
        />
      </header>

      <div className="price-board-table-wrap">
        <div className="price-board-colhead">
          <div className="colhead-spacer" />
          <div className="colhead-ccy" />
          <div className="colhead-sell">賣出</div>
          <div className="colhead-buy">買入</div>
        </div>

        {metalsForBoard.map((m, idx) => {
          const row = findByMetal(m.metal);
          const currencyRows =
            m.metal === "gold_gram"
              ? [
                  {
                    label: "MOP",
                    sell: row?.sell_mop,
                    buy: row?.buy_mop
                  },
                  {
                    label: "CNY",
                    sell: row?.sell_cny,
                    buy: row?.buy_cny
                  }
                ]
              : [
                  {
                    label: "HKD",
                    sell: row?.sell_hkd,
                    buy: row?.buy_hkd
                  },
                  {
                    label: "MOP",
                    sell: row?.sell_mop,
                    buy: row?.buy_mop
                  }
                ];
          return (
            <section className="metal-section" key={m.metal}>
              {idx > 0 ? <div className="metal-divider" /> : null}
              <div className="metal-grid">
                <div
                  className={`metal-title ${m.metal === "gold_gram" ? "metal-title--gram" : ""}`}
                >
                  <div className="metal-title-main">{m.label}</div>
                  {m.subLabel ? (
                    <div className="metal-title-sub">{m.subLabel}</div>
                  ) : null}
                </div>
                <div className="ccy-label">{currencyRows[0].label}</div>
                <div className="price-num sell">
                  <AnimatedPrice
                    loading={initialLoading}
                    value={currencyRows[0].sell}
                  />
                </div>
                <div className="price-num buy">
                  <AnimatedPrice
                    loading={initialLoading}
                    value={currencyRows[0].buy}
                  />
                </div>
                <div className="ccy-label">{currencyRows[1].label}</div>
                <div className="price-num sell">
                  <AnimatedPrice
                    loading={initialLoading}
                    value={currencyRows[1].sell}
                  />
                </div>
                <div className="price-num buy">
                  <AnimatedPrice
                    loading={initialLoading}
                    value={currencyRows[1].buy}
                  />
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {displayMode === "default" ? (
        <>
          <div className="price-board-wave" aria-hidden />

          <footer className="price-board-footer">
            {data?.date ? <div>是日金價：{data.date}</div> : null}
            <div>如有任何查詢請 Tel：2821 7017</div>
            <div>純金店珠寶金行</div>
            <AppMenu variant="inline" />
          </footer>
        </>
      ) : null}
    </div>
  );
}
